/**
 * ADR-61: the change-control constitution, as data and pure functions.
 *
 * Three planes: the runtime runs the auction; the control plane observes,
 * files changes, and will one day execute and validate them; the governance
 * plane says what the control plane may do. This module is the governance
 * plane's vocabulary: the human lines, the gates, the shape of a policy, the
 * risk score, and the classifier that reads a change's declared surface and
 * says which lines it crosses. Everything here is pure so it can be pinned
 * by tests the way `permissions.test.ts` pins every ring command against
 * every role: an executor or a surface without a classification does not
 * compile, and a line that is not exercised by a test is not a line.
 */

// ---------------------------------------------------------------------------
// The declared surface of a change: what it touches, in a closed vocabulary.
// Never free text. A change that touches nothing named here touches nothing
// the classifier can see, and is therefore NOT eligible for policy approval
// (an undeclared surface is a human line, not a free pass).
// ---------------------------------------------------------------------------

export const SURFACE_AREAS = [
  /** Payments, invoicing, settlement truth, Stripe. */
  'MONEY',
  /** The reducer: what it accepts, the outcome model, increments, fencing, acknowledgement gates. */
  'BIDDING_SEMANTICS',
  /** The fenced writer, the log, idempotency, the store. */
  'DURABILITY',
  /** An auction's format or a live sale's provisioned mode. */
  'AUCTION_FORMAT',
  /** Tokens, roles, permissions, allowlists, control bearers. */
  'SECURITY',
  /** A ring runtime policy flag (fanout policy, timeouts, coalescing) by configuration. */
  'RING_RUNTIME_FLAG',
  /** A code deployment of the control plane or the ring image. */
  'DEPLOYMENT',
  /** A database migration. */
  'MIGRATION',
  /** Policies, patterns, human lines, gate definitions, thresholds, eligibility, emergency authority. */
  'GOVERNANCE',
  /** Operator UI only: pages, wording, layout. */
  'OPERATOR_UI',
  /** Documentation only. */
  'DOCS',
] as const;
export type SurfaceArea = (typeof SURFACE_AREAS)[number];

export interface ChangeSurface {
  readonly areas: readonly SurfaceArea[];
  /** Tenants, sales and rings the change reaches at execution. */
  readonly blastRadius?: { readonly tenants: number; readonly sales: number; readonly rings: number };
  /** True when a rollback of this exact change was executed and validated on a rehearsal ring. */
  readonly rollbackProven?: boolean;
  /** The registered pattern this instance follows, or absent for a first instance. */
  readonly pattern?: string;
  /** True when bidding is open on any target at the planned execution time. */
  readonly biddingOpenAtExecution?: boolean;
  /** True when every validation criterion names a metric the platform observes now. */
  readonly telemetryCovered?: boolean;
}

// ---------------------------------------------------------------------------
// The human lines (ADR-61 section 3). A line is a name, a sentence, and a pure
// predicate over the declared surface and the policy in force.
// ---------------------------------------------------------------------------

export const HUMAN_LINE_KEYS = [
  'MONEY', 'BIDDING_SEMANTICS', 'DURABILITY', 'AUCTION_FORMAT', 'SECURITY',
  'BLAST_RADIUS', 'UNPROVEN_ROLLBACK', 'FIRST_INSTANCE', 'GOVERNANCE', 'UNDECLARED_SURFACE',
] as const;
export type HumanLineKey = (typeof HUMAN_LINE_KEYS)[number];

export interface PolicyLimits {
  /** The most tenants / sales / rings one execution may reach. */
  readonly blastRadius: { readonly tenants: number; readonly sales: number; readonly rings: number };
}

export interface HumanLine {
  readonly key: HumanLineKey;
  /** Section 3 numbering, for the record. */
  readonly number: number;
  readonly title: string;
  readonly crosses: (surface: ChangeSurface, limits: PolicyLimits | null) => boolean;
}

const has = (s: ChangeSurface, a: SurfaceArea): boolean => s.areas.includes(a);

/** Version 1 of the lines. A new version is a governance change (line 9), never an edit. */
export const HUMAN_LINES_V1: readonly HumanLine[] = [
  { key: 'MONEY', number: 1, title: 'touches money movement', crosses: (s) => has(s, 'MONEY') },
  { key: 'BIDDING_SEMANTICS', number: 2, title: 'changes bidding semantics', crosses: (s) => has(s, 'BIDDING_SEMANTICS') },
  { key: 'DURABILITY', number: 3, title: 'weakens durability', crosses: (s) => has(s, 'DURABILITY') || has(s, 'MIGRATION') },
  { key: 'AUCTION_FORMAT', number: 4, title: 'changes an auction format or a live provisioned mode', crosses: (s) => has(s, 'AUCTION_FORMAT') },
  { key: 'SECURITY', number: 5, title: 'changes auth or the security posture', crosses: (s) => has(s, 'SECURITY') },
  {
    key: 'BLAST_RADIUS', number: 6, title: 'exceeds the blast radius its policy allows',
    crosses: (s, limits) => {
      if (!s.blastRadius) return true; // undeclared reach is unbounded reach
      if (!limits) return true; // no policy, no limit, a human decides
      return s.blastRadius.tenants > limits.blastRadius.tenants || s.blastRadius.sales > limits.blastRadius.sales || s.blastRadius.rings > limits.blastRadius.rings;
    },
  },
  { key: 'UNPROVEN_ROLLBACK', number: 7, title: 'lacks a proven rollback', crosses: (s) => s.rollbackProven !== true },
  { key: 'FIRST_INSTANCE', number: 8, title: 'is the first instance of a pattern', crosses: (s) => !s.pattern },
  { key: 'GOVERNANCE', number: 9, title: 'alters what the control plane is permitted to do', crosses: (s) => has(s, 'GOVERNANCE') },
  { key: 'UNDECLARED_SURFACE', number: 10, title: 'declares no surface at all', crosses: (s) => s.areas.length === 0 },
];

export interface Classification {
  /** The lines crossed, in section order. Empty means eligible for policy approval under the limits given. */
  readonly lines: readonly HumanLineKey[];
  readonly governance: boolean;
  readonly humanRequired: boolean;
}

/** Pure. Which human lines does this surface cross under these policy limits? */
export function classifyChange(surface: ChangeSurface, limits: PolicyLimits | null, lines: readonly HumanLine[] = HUMAN_LINES_V1): Classification {
  const crossed = lines.filter((l) => l.crosses(surface, limits)).map((l) => l.key);
  return { lines: crossed, governance: crossed.includes('GOVERNANCE'), humanRequired: crossed.length > 0 };
}

/** Governance-plane actions, each a line-9 change by construction. */
export const GOVERNANCE_ACTIONS = ['POLICY_VERSION', 'PATTERN_REGISTERED', 'PATTERN_RETIRED', 'HUMAN_LINES_VERSION'] as const;
export type GovernanceAction = (typeof GOVERNANCE_ACTIONS)[number];

// ---------------------------------------------------------------------------
// Gates (ADR-61 section 5): names the platform evaluates from what it has.
// ---------------------------------------------------------------------------

export const GATES = [
  'TESTS_GREEN', 'BENCHMARK_CLEAN', 'REHEARSAL_CLEAN', 'ROLLBACK_PROVEN', 'TELEMETRY_PRESENT',
  'NO_ACTIVE_SEV1', 'AUCTION_GUARD', 'BLAST_RADIUS_WITHIN_POLICY', 'HUMAN_LINE_CLEAR',
] as const;
export type Gate = (typeof GATES)[number];
export const GATE_VERDICTS = ['PASS', 'FAIL', 'SKIPPED', 'NOT_APPLICABLE'] as const;
export type GateVerdict = (typeof GATE_VERDICTS)[number];

/** A gate the platform has not evaluated is not a pass (ADR-49: absence of evidence is never health). */
export function gatesSatisfied(required: readonly Gate[], evaluated: ReadonlyMap<Gate, GateVerdict>): { readonly satisfied: boolean; readonly missing: readonly Gate[]; readonly failed: readonly Gate[] } {
  const missing = required.filter((g) => !evaluated.has(g) || evaluated.get(g) === 'SKIPPED');
  const failed = required.filter((g) => evaluated.get(g) === 'FAIL');
  return { satisfied: missing.length === 0 && failed.length === 0, missing, failed };
}

// ---------------------------------------------------------------------------
// The approved surface, pinned (ADR-61 cut 3 review): an approval is given for
// exactly one surface. The executor recomputes this hash from the surface on
// the record at execution time and refuses when it differs. Same principle as
// policy-version pinning; without it an edit after APPROVED would inherit the
// approval.
// ---------------------------------------------------------------------------

/** The execution-relevant surface, normalised: sorted areas, the reach, the pattern and the three facts. Nothing else. */
export function canonicalSurface(surface: ChangeSurface): string {
  const areas = [...new Set(surface.areas)].sort();
  const br = surface.blastRadius ? { tenants: surface.blastRadius.tenants, sales: surface.blastRadius.sales, rings: surface.blastRadius.rings } : null;
  return JSON.stringify({ areas, blastRadius: br, pattern: surface.pattern ?? null, rollbackProven: surface.rollbackProven === true, telemetryCovered: surface.telemetryCovered === true, biddingOpenAtExecution: surface.biddingOpenAtExecution === true });
}

/** FNV-1a 64-bit over the canonical surface, hex. Deterministic, dependency-free, the same in the browser and on the server. */
export function surfaceHash(surface: ChangeSurface): string {
  const s = canonicalSurface(surface);
  let h = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let i = 0; i < s.length; i++) {
    h ^= BigInt(s.charCodeAt(i));
    h = (h * prime) & mask;
  }
  return 'fnv1a64:' + h.toString(16).padStart(16, '0');
}

// ---------------------------------------------------------------------------
// A policy (ADR-61 section 7): versioned data the engine reads and never writes.
// ---------------------------------------------------------------------------

export interface PolicyDefinition {
  readonly requiredGates: readonly Gate[];
  /** Policy approval is refused above this score, whatever the gates say. */
  readonly riskCeiling: number;
  readonly limits: PolicyLimits;
  readonly validation: { readonly windowMs: number; readonly criteria: readonly string[] };
  /** In order; the engine tries each until one validates. */
  readonly rollbackSteps: readonly string[];
  readonly escalation: { readonly rollbackOwner: string; readonly pageOnFailure: boolean };
  /** The surface areas an instance may declare; anything else crosses a line. */
  readonly eligibleAreas: readonly SurfaceArea[];
}

// ---------------------------------------------------------------------------
// The risk score (ADR-61 section 6): additive, 0..100, from facts on the record.
// ---------------------------------------------------------------------------

export interface RiskInputs {
  readonly surface: ChangeSurface;
  readonly limits: PolicyLimits | null;
  readonly biddersConnected: number;
}

export interface RiskScore {
  readonly score: number;
  readonly terms: readonly { readonly term: string; readonly points: number; readonly because: string }[];
}

export function riskScore(input: RiskInputs): RiskScore {
  const terms: { term: string; points: number; because: string }[] = [];
  const s = input.surface;
  const c = classifyChange(s, input.limits);
  const hard = c.lines.filter((l) => ['MONEY', 'BIDDING_SEMANTICS', 'DURABILITY', 'AUCTION_FORMAT', 'SECURITY', 'GOVERNANCE', 'UNDECLARED_SURFACE'].includes(l));
  if (hard.length > 0) terms.push({ term: 'surface', points: 100, because: `crosses ${hard.join(', ')}` });
  else {
    const areaPoints: Partial<Record<SurfaceArea, number>> = { RING_RUNTIME_FLAG: 10, DEPLOYMENT: 25, OPERATOR_UI: 5, DOCS: 0 };
    const p = Math.max(0, ...s.areas.map((a) => areaPoints[a] ?? 25));
    terms.push({ term: 'surface', points: p, because: s.areas.join(', ') || 'nothing declared' });
  }
  const br = s.blastRadius;
  const reach = br ? Math.min(30, br.rings * 5 + br.sales * 5 + Math.max(0, br.tenants - 1) * 10) : 30;
  terms.push({ term: 'blastRadius', points: reach, because: br ? `${br.tenants} tenant(s), ${br.sales} sale(s), ${br.rings} ring(s)` : 'undeclared reach' });
  const crowd = Math.min(20, Math.floor(input.biddersConnected / 100));
  terms.push({ term: 'crowd', points: crowd, because: `${input.biddersConnected} bidder(s) connected at execution` });
  terms.push({ term: 'reversibility', points: s.rollbackProven ? 0 : 100, because: s.rollbackProven ? 'rollback executed and validated on a rehearsal' : 'rollback not proven' });
  terms.push({ term: 'novelty', points: s.pattern ? 0 : 20, because: s.pattern ? `registered pattern ${s.pattern}` : 'first instance' });
  terms.push({ term: 'timing', points: s.biddingOpenAtExecution ? 40 : 0, because: s.biddingOpenAtExecution ? 'bidding open on a target at execution' : 'between lots' });
  terms.push({ term: 'telemetry', points: s.telemetryCovered ? 0 : 30, because: s.telemetryCovered ? 'every criterion has a metric' : 'a criterion without a metric' });
  const score = Math.min(100, terms.reduce((a, t) => a + t.points, 0));
  return { score, terms };
}

// ---------------------------------------------------------------------------
// Approval (ADR-61 section 7): HUMAN or POLICY, with the evidence attached.
// ---------------------------------------------------------------------------

export const APPROVAL_METHODS = ['HUMAN', 'POLICY'] as const;
export type ApprovalMethod = (typeof APPROVAL_METHODS)[number];
export const CHANGE_ENGINE_IDENTITY = 'HIPNUMBER_CHANGE_ENGINE';

export interface ApprovalRecord {
  readonly method: ApprovalMethod;
  readonly by: string;
  readonly reason: string;
  readonly at: string;
  readonly lines: readonly HumanLineKey[];
  readonly policy?: string;
  readonly policyVersion?: number;
  readonly riskScore?: number;
  readonly requiredGates?: readonly Gate[];
  readonly passedGates?: readonly Gate[];
  readonly rollbackVerified?: boolean;
  readonly canaryPassed?: boolean;
  readonly authorizedBy?: string;
}

/**
 * Cut 1's one decision: may this approval be recorded? A HUMAN approval by a
 * named person may always be recorded (the human decides). A POLICY approval
 * is refused whenever a human line is crossed, whenever the approver is not
 * the engine, and (cut 1) always, because the engine that would hold that
 * authority is not yet operational; the refusal names why.
 */
export function approvalAllowed(record: Pick<ApprovalRecord, 'method' | 'by'>, classification: Classification, engineOperational: boolean): { readonly allowed: boolean; readonly because: string | null } {
  if (record.method === 'HUMAN') {
    if (!record.by || record.by === CHANGE_ENGINE_IDENTITY) return { allowed: false, because: 'a HUMAN approval needs a named person' };
    return { allowed: true, because: null };
  }
  if (classification.humanRequired) return { allowed: false, because: `human line crossed: ${classification.lines.join(', ')}` };
  if (record.by !== CHANGE_ENGINE_IDENTITY) return { allowed: false, because: 'a POLICY approval is recorded by the change engine only' };
  if (!engineOperational) return { allowed: false, because: 'the change engine is not operational (ADR-61 cut 1); POLICY approval cannot be claimed before the policy engine that provides that authority exists' };
  return { allowed: true, because: null };
}
